import { InfoTip, Tag } from '@dev-spendesk/grapes';
import cx from 'classnames';

import { useFeature } from 'common/hooks/useFeature';
import { useTranslation } from 'common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';

import { daysToString, formatHour } from './formatter';
import { type ApiCardSettings } from '../../../card';
import {
  merchantCategoryToTranslationKey,
  merchantCategoryOrder,
} from '../../../merchantCategory';

import './PlasticCardControlSettings.css';

type Props = {
  cardSettings: ApiCardSettings;
};

export const PlasticCardControlSettings = ({ cardSettings }: Props) => {
  const { t } = useTranslation('global');
  const hasAdvancedCardControlFeature = useFeature(
    FEATURES.PHYSICAL_CARD_CONTROL_ADVANCED,
  );
  const hasCardAuthorisations =
    Object.keys(cardSettings.authorisations).length > 0;

  if (!hasCardAuthorisations && !hasAdvancedCardControlFeature) {
    return null;
  }

  const { isCashWithdrawalEnabled, isOnlineEnabled, isContactlessEnabled } =
    cardSettings.authorisations;

  return (
    <>
      <h2 className="text-content-primary heading-l mt-32 mb-24">
        {t('cards.controlSettings.yourCardSetup')}
      </h2>
      <div className="box flex">
        {(hasCardAuthorisations ||
          (hasAdvancedCardControlFeature && cardSettings.schedule)) && (
          <div className="PlasticCardControlSettings__column">
            <h5 className="PlasticCardControlSettings__title title-m pb-12">
              {t('cards.controlSettings.sections.authorisations.title')}
              <InfoTip
                content={t(
                  'cards.controlSettings.sections.authorisations.tipMessage',
                )}
              />
            </h5>
            {hasAdvancedCardControlFeature && cardSettings.schedule && (
              <>
                <div className="PlasticCardControlSettings__row body-m">
                  {t('cards.controlSettings.sections.schedule.dayOfUse')}
                  <Tag variant="info">
                    {daysToString(cardSettings.schedule.days, t)}
                  </Tag>
                </div>
                {!cardSettings.schedule.fullDay && (
                  <div className="PlasticCardControlSettings__row body-m">
                    {t('cards.controlSettings.sections.schedule.hoursOfUse')}
                    <Tag variant="neutral">
                      {formatHour(cardSettings.schedule.hours.from)}{' '}
                      {t('cards.controlSettings.sections.schedule.to')}{' '}
                      {formatHour(cardSettings.schedule.hours.to)}
                    </Tag>
                  </div>
                )}
              </>
            )}
            {hasCardAuthorisations && (
              <>
                {isCashWithdrawalEnabled !== undefined && (
                  <div className="PlasticCardControlSettings__row body-m">
                    {t(
                      'cards.controlSettings.sections.authorisations.cashWithdrawal',
                    )}
                    <Tag
                      variant={isCashWithdrawalEnabled ? 'success' : 'alert'}
                      iconName={
                        isCashWithdrawalEnabled
                          ? 'circle-check'
                          : 'hexagone-cross'
                      }
                    >
                      {isCashWithdrawalEnabled
                        ? t(
                            'cards.controlSettings.sections.authorisations.allowed',
                          )
                        : t(
                            'cards.controlSettings.sections.authorisations.notAllowed',
                          )}
                    </Tag>
                  </div>
                )}
                {isOnlineEnabled !== undefined && (
                  <div className="PlasticCardControlSettings__row body-m">
                    {t(
                      'cards.controlSettings.sections.authorisations.onlinePayment',
                    )}
                    <Tag
                      variant={isOnlineEnabled ? 'success' : 'alert'}
                      iconName={
                        isOnlineEnabled ? 'circle-check' : 'hexagone-cross'
                      }
                    >
                      {isOnlineEnabled
                        ? t(
                            'cards.controlSettings.sections.authorisations.allowed',
                          )
                        : t(
                            'cards.controlSettings.sections.authorisations.notAllowed',
                          )}
                    </Tag>
                  </div>
                )}
                {isContactlessEnabled !== undefined && (
                  <div className="PlasticCardControlSettings__row body-m">
                    {t(
                      'cards.controlSettings.sections.authorisations.contactlessPayment',
                    )}
                    <Tag
                      variant={isContactlessEnabled ? 'success' : 'alert'}
                      iconName={
                        isContactlessEnabled ? 'circle-check' : 'hexagone-cross'
                      }
                    >
                      {isContactlessEnabled
                        ? t(
                            'cards.controlSettings.sections.authorisations.allowed',
                          )
                        : t(
                            'cards.controlSettings.sections.authorisations.notAllowed',
                          )}
                    </Tag>
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {hasAdvancedCardControlFeature && (
          <div
            className={cx('PlasticCardControlSettings__column', {
              'border-l-border-default ml-24 border-0 border-l border-solid pl-24':
                hasCardAuthorisations || cardSettings.schedule,
            })}
          >
            <h5 className="PlasticCardControlSettings__title title-m pb-12">
              {t('cards.controlSettings.sections.categories.title')}
            </h5>
            {merchantCategoryOrder.map((merchantCategory) => {
              const isAllowed = cardSettings.categories.find(
                (category) => category.identifier === merchantCategory,
              )?.allowed;
              return (
                <div
                  key={merchantCategory}
                  className="PlasticCardControlSettings__row body-m"
                >
                  {t(merchantCategoryToTranslationKey[merchantCategory])}
                  <Tag
                    variant={isAllowed ? 'success' : 'alert'}
                    iconName={isAllowed ? 'circle-check' : 'hexagone-cross'}
                  >
                    {isAllowed
                      ? t(
                          'cards.controlSettings.sections.authorisations.allowed',
                        )
                      : t(
                          'cards.controlSettings.sections.authorisations.notAllowed',
                        )}
                  </Tag>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};
